import React, { useState, useContext, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  Input,
  Select,
  Radio,
  DatePicker,
  Checkbox,
  Upload,
  Button,
  Row,
  Col,
  Typography,
  Divider,
  TimePicker,
  Tooltip,
  notification,
} from "antd";
import {
  HomeOutlined,
  ToolOutlined,
  FireOutlined,
  SmileOutlined,
  AppstoreOutlined,
  CarOutlined,
  SyncOutlined,
  HddOutlined,
  SettingOutlined,
  ApartmentOutlined,
  BuildOutlined,
  InboxOutlined,
  PlusCircleOutlined,
  CloudOutlined,
  PlusOutlined,
  CheckCircleFilled,
  UserOutlined,
  PhoneOutlined,
  WifiOutlined,
  ThunderboltOutlined,
  BulbOutlined,
  DeploymentUnitOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import useSaveUnit from "../../../hooks/landlord/property/useSaveUnit";
import { openError, openSuccess } from "../../../utils/helper/message";
import { UserContext } from "../../../context/UserContext";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const initialState = {
  unitnumber: "",
  bedrooms: "",
  bathrooms: "",
  size: "",
  parkingType: "",
  rent: "",
  deposit: "",
  moveInFee: "",
  parkingFee: "",
  lateRentFee: "",
  moveOutFee: "",
  dateAvailable: null,
  leaseDuration: "",
  petsAllowed: true,
  petOptions: {
    largeDogs: true,
    smallDogs: true,
    cats: true,
  },
  petDeposit: "",
  monthlyPetRent: "",
  photos: [],
  amenities: [],
  utilities: [],
  title: "",
  description: "",
  showingDateAvailable: null,
  showingTimeAvailableFrom: "",
  showingTimeAvailableTo: "",
  showingDuration: "",
};

const AddUnitPage = () => {
  const { user } = useContext(UserContext);
  const { id } = useParams();
  const isEdit = !!id;
  const { getUnitById } = useSaveUnit();
  const [formData, setFormData] = useState({ ...initialState });

  const { saveUnit, loading, error } = useSaveUnit();
  const [api, contextHolder] = notification.useNotification();
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) {
      setFormData({ ...initialState });
    }
  }, [id]);

  useEffect(() => {
    const fetchUnit = async () => {
      if (id) {
        const unit = await getUnitById(id, user?.uid);
        if (unit) {
          const mappedPhotos = unit.photos?.map((url, index) => ({
            uid: String(index),
            name: `photo-${index}`,
            status: "done",
            url,
          }));

          setFormData({
            ...initialState,
            ...unit,
            photos: mappedPhotos || [],
            amenities: unit.amenities || [],
            utilities: unit.utilities || [],
          });

          console.log("Set form data after fetch:", {
            amenities: unit.amenities,
            utilities: unit.utilities,
          });
        }
      }
    };
    fetchUnit();
  }, []);

  const amenityList = [
    "Wheelchair Access",
    "Balcony",
    "Central Air",
    "Central Heat",
    "Deck",
    "Dishwasher",
    "Hardwood Floors",
    "Pool",
    "Stainless Steel Appliances",
    "Updated Kitchen",
    "Updated Bathroom",
    "Storage",
    "Laundry In Unit",
    "Laundry In Building",
    "Yard",
  ];

  const amenityIcons = {
    "Wheelchair Accessible": <SmileOutlined />,
    Balcony: <HomeOutlined />,
    "Central Air": <CloudOutlined />,
    "Central Heat": <FireOutlined />,
    Deck: <ApartmentOutlined />,
    Dishwasher: <ToolOutlined />,
    "Hardwood Floors": <AppstoreOutlined />,
    Pool: <InboxOutlined />,
    "Stainless Steel Appliance": <SettingOutlined />,
    "Updated Kitchen": <BuildOutlined />,
    "Updated Bathroom": <BuildOutlined />,
    Storage: <HddOutlined />,
    "In-Unit Laundry": <SyncOutlined />,
    "Laundry In Building": <SyncOutlined />,
    Yard: <CarOutlined />,
  };

  const utilityList = [
    { key: "ac", label: "A/C", icon: <CloudOutlined /> },
    { key: "cable", label: "Cable", icon: <DeploymentUnitOutlined /> },
    { key: "electricity", label: "Electricity", icon: <BulbOutlined /> },
    { key: "gas", label: "Gas", icon: <AppstoreOutlined /> },
    { key: "heat", label: "Heat", icon: <FireOutlined /> },
    { key: "internet", label: "Internet", icon: <WifiOutlined /> },
    { key: "water", label: "Water", icon: <ThunderboltOutlined /> },
  ];

  const toggleUtility = (key) => {
    setFormData((prev) => {
      const current = prev.utilities || [];
      const updated = current.includes(key)
        ? current.filter((u) => u !== key)
        : [...current, key];
      return { ...prev, utilities: updated };
    });
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePetOptionChange = (key, checked) => {
    setFormData((prev) => ({
      ...prev,
      petOptions: {
        ...prev.petOptions,
        [key]: checked,
      },
    }));
  };

  const toggleAmenity = (name) => {
    setFormData((prev) => {
      const exists = prev.amenities.includes(name);
      const amenities = exists
        ? prev.amenities.filter((a) => a !== name)
        : [...prev.amenities, name];
      return { ...prev, amenities };
    });

    console.log("Updated amenities:", formData.amenities);
  };

  const handleSaveUnit = async (status = "Unlisted") => {
    const requiredFields = [
      { field: formData.unitnumber, name: "unitnumber" },
      { field: formData.bedrooms, name: "Bedrooms" },
      { field: formData.bathrooms, name: "Bathrooms" },
      { field: formData.rent, name: "Rent Amount" },
      { field: formData.title, name: "Title" },
      { field: formData.description, name: "Description" },
      { field: formData.dateAvailable, name: "Date Available" },
    ];

    const missing = requiredFields.filter(
      ({ field }) => !field || field === ""
    );

    if (missing.length > 0) {
      openError(
        api,
        `Please fill the following required fields: ${missing
          .map((m) => m.name)
          .join(", ")}`
      );
      return;
    }

    console.log("PHOTOS:", formData.photos.length);

    if (formData.photos.length === 0) {
      openError(api, "Please select at least one Photo");
      return;
    }

    const payload = {
      ...formData,
      title: formData.title || "",
      description: formData.description || "",
      status,
    };

    console.log(`Saving unit with status: ${status}`, payload);

    const result = await saveUnit(payload, isEdit ? id : null);

    if (result.success) {
      openSuccess(
        api,
        `Unit ${status === "Listed" ? "Listed" : "saved"} successfully`
      );

      if (!id) {
        setFormData({ ...initialState });
      }
    } else {
      openError(api, `Failed to ${status === "list" ? "list" : "save"} unit`);
    }
  };

  const handleCancel = () => {
    navigate("/landlord/property/units");
  };

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      {contextHolder}
      <Title level={4}>Unit</Title>
      <Row gutter={16}>
        <Col span={5}>
          <Input
            type="text"
            placeholder="Unit *"
            value={formData.unitnumber}
            onChange={(e) => handleChange("unitnumber", e.target.value)}
          />
        </Col>
        {/* Bedrooms dropdown */}
        <Col span={4}>
          <Select
            placeholder="Bedrooms *"
            value={formData.bedrooms || undefined}
            onChange={(value) => handleChange("bedrooms", value)}
            style={{ width: "100%" }}
          >
            <Option value="Studio">Studio</Option>
            <Option value="Loft">Loft</Option>
            <Option value="1 Bedroom">1 Bedroom</Option>
            <Option value="2 Bedrooms">2 Bedrooms</Option>
            <Option value="3 Bedrooms">3 Bedrooms</Option>
            <Option value="4 Bedrooms">4 Bedrooms</Option>
            <Option value="5+ Bedrooms">5+ Bedrooms</Option>
          </Select>
        </Col>
        <Col span={4}>
          <Input
            type="number"
            placeholder="Bathrooms *"
            value={formData.bathrooms}
            onChange={(e) => handleChange("bathrooms", e.target.value)}
          />
        </Col>
        <Col span={4} style={{ marginTop: 0 }}>
          <Input
            type="number"
            placeholder="Property Size (sqft)"
            value={formData.size}
            onChange={(e) => handleChange("size", e.target.value)}
          />
        </Col>
        <Col span={4} style={{ marginTop: 0 }}>
          <Select
            placeholder="Parking Type"
            value={formData.parkingType || undefined}
            onChange={(value) => handleChange("parkingType", value)}
            style={{ width: "100%" }}
          >
            <Option value="Garage">Garage</Option>
            <Option value="Street">Street</Option>
            <Option value="Driveway">Driveway</Option>
            <Option value="None">None</Option>
          </Select>
        </Col>
      </Row>

      <Divider />
      <Title level={4}>Terms</Title>
      <Row gutter={16}>
        <Col span={6}>
          <Input
            prefix="$"
            placeholder="Rent Amount (Monthly) *"
            type="number"
            value={formData.rent}
            onChange={(e) => handleChange("rent", e.target.value)}
            style={{ height: "31px" }}
          />
        </Col>
        <Col span={6}>
          <Input
            prefix="$"
            placeholder="Security Deposit"
            type="number"
            value={formData.deposit}
            onChange={(e) => handleChange("deposit", e.target.value)}
            style={{ height: "31px" }}
          />
        </Col>
        <Col span={6} style={{ marginTop: 0 }}>
          <DatePicker
            style={{ width: "100%", height: "31px" }}
            placeholder="Date Available *"
            value={
              formData.dateAvailable ? dayjs(formData.dateAvailable) : null
            }
            onChange={(_, dateString) =>
              handleChange("dateAvailable", dateString)
            }
          />
        </Col>
        <Col span={6} style={{ marginTop: 0 }}>
          <Select
            placeholder="Lease Duration"
            value={formData.leaseDuration || undefined}
            onChange={(value) => handleChange("leaseDuration", value)}
            style={{ width: "100%", height: "31px" }}
          >
            <Option value="6 Months">6 Months</Option>
            <Option value="1 Year">1 Year</Option>
            <Option value="2 Years">2 Years</Option>
          </Select>
        </Col>
        <Col span={6} style={{ marginTop: 16 }}>
          <Input
            prefix="$"
            placeholder="Parking Fee"
            type="number"
            value={formData.parkingFee}
            onChange={(e) => handleChange("parkingFee", e.target.value)}
            style={{ height: "31px" }}
          />
        </Col>
        <Col span={6} style={{ marginTop: 16 }}>
          <Input
            prefix="$"
            placeholder="Late Rent Fee"
            type="number"
            value={formData.lateRentFee}
            onChange={(e) => handleChange("lateRentFee", e.target.value)}
            style={{ height: "31px" }}
          />
        </Col>
        <Col span={6} style={{ marginTop: 16 }}>
          <Input
            prefix="$"
            placeholder="Move In Fee"
            type="number"
            value={formData.moveInFee}
            onChange={(e) => handleChange("moveInFee", e.target.value)}
            style={{ height: "31px" }}
          />
        </Col>
        <Col span={6} style={{ marginTop: 16 }}>
          <Input
            prefix="$"
            placeholder="Move Out Fee"
            type="number"
            value={formData.moveOutFee}
            onChange={(e) => handleChange("moveOutFee", e.target.value)}
            style={{ height: "31px" }}
          />
        </Col>
      </Row>
      <Divider />
      <Title level={4}>Pets</Title>
      <Radio.Group
        value={formData.petsAllowed}
        onChange={(e) => handleChange("petsAllowed", e.target.value)}
      >
        <Radio value={false}>No Pets Allowed</Radio>
        <Radio value={true}>Pets Allowed</Radio>
      </Radio.Group>
      {formData.petsAllowed && (
        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col>
            <Checkbox
              checked={formData.petOptions.largeDogs}
              onChange={(e) =>
                handlePetOptionChange("largeDogs", e.target.checked)
              }
            >
              Large Dogs Allowed
            </Checkbox>
          </Col>
          <Col>
            <Checkbox
              checked={formData.petOptions.smallDogs}
              onChange={(e) =>
                handlePetOptionChange("smallDogs", e.target.checked)
              }
            >
              Small Dogs Allowed
            </Checkbox>
          </Col>
          <Col>
            <Checkbox
              checked={formData.petOptions.cats}
              onChange={(e) => handlePetOptionChange("cats", e.target.checked)}
            >
              Cats Allowed
            </Checkbox>
          </Col>
          <Col span={4} style={{ marginTop: 0 }}>
            <Input
              prefix="$"
              placeholder="Pet Deposit"
              type="number"
              value={formData.petDeposit}
              onChange={(e) => handleChange("petDeposit", e.target.value)}
              style={{ height: "31px" }}
            />
          </Col>
          <Col span={4} style={{ marginTop: 0 }}>
            <Input
              prefix="$"
              placeholder="Monthly Pet Rent"
              type="number"
              value={formData.monthlyPetRent}
              onChange={(e) => handleChange("monthlyPetRent", e.target.value)}
              style={{ height: "31px" }}
            />
          </Col>
        </Row>
      )}
      <Divider />
      <Title level={4}>Photos</Title>
      <Upload
        listType="picture-card"
        fileList={formData.photos}
        accept="image/*"
        onChange={({ fileList }) => handleChange("photos", fileList)}
        customRequest={({ onSuccess }) => {
          setTimeout(() => onSuccess("ok"), 0);
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            fontSize: 14,
          }}
        >
          <PlusOutlined
            style={{ fontSize: 28, fontWeight: "bold", color: "gray" }}
          />
          <div style={{ marginTop: 4 }}>Add More</div>
        </div>
      </Upload>

      <Divider />
      <Title level={4}>Amenities</Title>
      <Row gutter={[12, 12]} justify="center">
        {amenityList.map((a) => {
          const selected = formData.amenities?.includes(a);
          return (
            <Col
              key={a}
              xs={12}
              sm={8}
              md={6}
              lg={4}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <div
                onClick={() => toggleAmenity(a)}
                style={{
                  cursor: "pointer",
                  border: "2px solid",
                  borderColor: selected ? "#9701fc" : "#cccccc",
                  borderRadius: "50%",
                  width: 64,
                  height: 64,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                {selected && (
                  <CheckCircleFilled
                    style={{
                      color: "#9701fc",
                      position: "absolute",
                      top: -10,
                      right: 0,
                      fontSize: 24,
                      background: "#ffffff",
                      borderRadius: "50%",
                    }}
                  />
                )}
                <div
                  style={{
                    fontSize: 24,
                    color: selected ? "#000000" : "#cccccc",
                  }}
                >
                  {amenityIcons[a] || <PlusCircleOutlined />}
                </div>
              </div>
              <div
                style={{
                  marginTop: 8,
                  color: selected ? "#000000" : "#999999",
                  textAlign: "center",
                }}
              >
                {a}
              </div>
            </Col>
          );
        })}
      </Row>

      <Divider />
      <Title level={4}>Utilities</Title>
      <Row gutter={[12, 12]}>
        {utilityList.map((util) => {
          const selected = formData.utilities?.includes(util.key);
          return (
            <Col key={util.key}>
              <div style={{ position: "relative", textAlign: "center" }}>
                <div
                  onClick={() => toggleUtility(util.key)}
                  style={{
                    textAlign: "center",
                    cursor: "pointer",
                    border: "2px solid",
                    borderColor: selected ? "#9701fc" : "#cccccc",
                    borderRadius: "50%",
                    width: 64,
                    height: 64,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    margin: "auto",
                  }}
                >
                  {selected && (
                    <CheckCircleFilled
                      style={{
                        color: "#9701fc",
                        position: "absolute",
                        top: -10,
                        right: 0,
                        fontSize: 24,
                        background: "#ffffff",
                        borderRadius: "50%",
                      }}
                    />
                  )}
                  <div
                    style={{
                      fontSize: 24,
                      color: selected ? "#000000" : "#cccccc",
                    }}
                  >
                    {util.icon}
                  </div>
                </div>
                <div
                  style={{
                    marginTop: 8,
                    color: selected ? "#000000" : "#999999",
                  }}
                >
                  {util.label}
                </div>
              </div>
            </Col>
          );
        })}
      </Row>

      <Divider />
      <Title level={4}>Contact Information</Title>
      <Text type="secondary">
        All name and phone number contact information is saved to your Account
        Settings.
      </Text>
      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={12}>
          <div
            style={{
              border: "2px solid gray",
              padding: "12px 16px",
            }}
          >
            <Text strong>
              <UserOutlined style={{ marginRight: 8 }} />
              Display Name <span style={{ color: "red" }}>*</span>
            </Text>
            <div style={{ marginTop: 4 }}>
              {user?.fullName || user?.firstName + " " + user?.lastName}
            </div>
          </div>
        </Col>
        <Col span={12}>
          <div
            style={{
              border: "2px solid gray",
              padding: "12px 16px",
            }}
          >
            <Text strong>
              <PhoneOutlined style={{ marginRight: 8 }} />
              Display Phone <span style={{ color: "red" }}>*</span>
            </Text>
            <div style={{ marginTop: 4 }}>{user?.phone || "+1 5555555555"}</div>
          </div>
        </Col>
      </Row>

      <Divider />
      <Title level={4}>Title & Description</Title>
      <Row gutter={16} align="middle">
        <Col span={24}>
          <>
            <Title level={5}>
              Title <span style={{ color: "red" }}>*</span>
            </Title>
            <Input
              placeholder="$1,500 - 1 Bedroom 1 Bathroom Apartment In Los Angeles With Great
              Amenities"
              type="text"
              value={formData.title}
              name="title"
              onChange={(e) => handleChange("title", e.target.value)}
            />
          </>
        </Col>

        <Col span={24} style={{ marginTop: 16 }}>
          <>
            <Title level={5}>
              Description <span style={{ color: "red" }}>*</span>
            </Title>
            <TextArea
              name="description"
              autoSize={{ minRows: 5 }}
              placeholder={`Amazing 1 bedroom, 1 bathroom apartment in Los Angeles. Amenities included: wheelchair accessible, balcony, central air, central heat, deck, dishwasher, hardwood floors, pool, stainless steel appliance, washer dryer, and yard. Utilities included: cable, electricity, heat, gas, internet and air conditioning. Is not pet friendly. Date Available: Jun 1st 2025. $1,500/month rent. Please submit the form on this page or contact testq at 415-340-2545 to learn more. This property is managed by a responsible landlord using Avail landlord software.`}
              onChange={(e) => handleChange("description", e.target.value)}
              value={formData.description}
            />
          </>
        </Col>
      </Row>

      <Divider />
      <Title level={4}>Showings</Title>
      <Row gutter={16} align="middle">
        <Col span={24}>
          <Text type="secondary">
            Add your availability that tenants can book for showings. You can
            delete added time slots, but not if they have scheduled showings -
            cancel the showings first.
          </Text>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col style={{ width: 180 }}>
          <label style={{ display: "block", marginBottom: 8, color: "gray" }}>
            Available Date
          </label>
          <DatePicker
            placeholder="Available Date"
            style={{ width: "100%", height: 40 }}
            value={
              formData.showingDateAvailable
                ? dayjs(formData.showingDateAvailable)
                : null
            }
            onChange={(_, dateString) =>
              handleChange("showingDateAvailable", dateString)
            }
          />
        </Col>

        <Col style={{ width: 140 }}>
          <label style={{ display: "block", marginBottom: 8, color: "gray" }}>
            Available Time
          </label>
          <TimePicker
            use12Hours
            format="h:mm A"
            placeholder="From"
            style={{ width: "100%", height: 40 }}
            onChange={(_, timeString) =>
              handleChange("showingTimeAvailableFrom", timeString)
            }
            value={
              formData.showingTimeAvailableFrom
                ? dayjs(formData.showingTimeAvailableFrom, "h:mm A")
                : null
            }
          />
        </Col>

        <Col style={{ width: 140 }}>
          <label style={{ display: "block", marginBottom: 8, color: "gray" }}>
            To
          </label>
          <TimePicker
            use12Hours
            format="h:mm A"
            placeholder="To"
            style={{ width: "100%", height: 40 }}
            onChange={(_, timeString) =>
              handleChange("showingTimeAvailableTo", timeString)
            }
            value={
              formData.showingTimeAvailableTo
                ? dayjs(formData.showingTimeAvailableTo, "h:mm A")
                : null
            }
          />
        </Col>

        <Col style={{ width: 160 }}>
          <label style={{ display: "block", marginBottom: 8, color: "gray" }}>
            Showing Duration
          </label>
          <Input
            suffix={
              <Tooltip title="Duration per showing">
                <InfoCircleOutlined />
              </Tooltip>
            }
            placeholder="60 min"
            style={{ width: "100%", height: 40 }}
            onChange={(e) => handleChange("showingDuration", e.target.value)}
          />
        </Col>
      </Row>

      <Divider />
      <Row gutter={16}>
        <Col span={24} style={{ textAlign: "right" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 12,
              marginTop: 32,
            }}
          >
            <Button
              style={{
                border: 0,
                color: "#9701fc",
                textTransform: "uppercase",
              }}
              htmlType="button"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              style={{
                backgroundColor: "#fff",
                borderColor: "#9701fc",
                color: "#9701fc",
                textTransform: "uppercase",
              }}
              loading={loading}
              onClick={() => handleSaveUnit("Unlisted")}
            >
              Save and Close
            </Button>
            <Button
              type="primary"
              style={{ background: "#9701fc" }}
              loading={loading}
              onClick={() => handleSaveUnit("Listed")}
            >
              List Unit
            </Button>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default AddUnitPage;
